import React from "react";

/**
 * PrinterScene — the animated SVG 3D-printer scene used inside HeroBackground.
 * Purely decorative; kept in its own file to keep HeroBackground under the
 * component-size threshold.
 */
export default function PrinterScene() {
  return (
    <svg
      className="absolute inset-0 w-full h-full"
      viewBox="0 0 1600 900"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <Defs />
      <FloorGrid />
      <AmbientOrbs />
      <PrinterCage />
      <GantryRail />
      <PrintBed />
      <PrintLayers />
      <FilamentSpool />
      <NozzleHead />
      <Particles />
    </svg>
  );
}

const Defs = () => (
  <defs>
    <linearGradient id="grad-orange" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#FF7A45" />
      <stop offset="100%" stopColor="#F4511E" />
    </linearGradient>
    <linearGradient id="grad-white" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="rgba(255,255,255,0.35)" />
      <stop offset="100%" stopColor="rgba(255,255,255,0.05)" />
    </linearGradient>
    <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="4" result="b" />
      <feMerge>
        <feMergeNode in="b" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
    <path id="nozzle-path" d="M 780 460 Q 900 440 1020 460 T 1260 460 T 1020 480 T 780 480 Z" />
  </defs>
);

const FloorGrid = () => (
  <g className="floor-drift" opacity="0.25">
    {Array.from({ length: 22 }).map((_, i) => {
      const y = 520 + i * 20;
      const half = 480 * (1 + i * 0.06);
      return (
        <line key={`fh-${i}`} x1={800 - half} y1={y} x2={800 + half} y2={y}
          stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
      );
    })}
    {Array.from({ length: 18 }).map((_, i) => {
      const t = (i - 8.5) / 8.5;
      return (
        <line key={`fv-${i}`} x1={800 + t * 480} y1={520} x2={800 + t * 900} y2={880}
          stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
      );
    })}
  </g>
);

const AmbientOrbs = () => (
  <g opacity="0.6">
    <circle cx="220" cy="180" r="140" fill="#FF5722" opacity="0.06" filter="url(#glow)" />
    <circle cx="1380" cy="720" r="180" fill="#FF5722" opacity="0.07" filter="url(#glow)" />
  </g>
);

const PrinterCage = () => (
  <g stroke="rgba(255,255,255,0.28)" strokeWidth="2" fill="none">
    <path d="M 640 720 L 1360 720 L 1440 680 L 720 680 Z" fill="rgba(255,255,255,0.02)" />
    <line x1="720" y1="680" x2="720" y2="240" />
    <line x1="1440" y1="680" x2="1440" y2="240" />
    <line x1="640" y1="720" x2="640" y2="280" strokeDasharray="4 6" opacity="0.5" />
    <line x1="1360" y1="720" x2="1360" y2="280" strokeDasharray="4 6" opacity="0.5" />
    <path d="M 640 280 L 1360 280 L 1440 240 L 720 240 Z" />
    <line x1="720" y1="240" x2="640" y2="280" opacity="0.4" />
    <line x1="1440" y1="240" x2="1360" y2="280" opacity="0.4" />
  </g>
);

const GantryRail = () => (
  <g>
    <rect x="700" y="410" width="760" height="6" fill="rgba(255,255,255,0.4)" rx="1" />
    <rect x="700" y="410" width="760" height="6" fill="url(#grad-white)" opacity="0.3" />
  </g>
);

const PrintBed = () => (
  <g>
    <path d="M 740 620 L 1400 620 L 1470 590 L 810 590 Z"
      fill="rgba(255,87,34,0.05)" stroke="rgba(255,87,34,0.4)" strokeWidth="1.5" />
    <path d="M 740 620 L 810 590 L 810 596 L 740 626 Z" fill="rgba(255,255,255,0.06)" />
  </g>
);

const PrintLayers = () => {
  const dur = 6.4;
  return (
    <g filter="url(#glow)">
      {Array.from({ length: 18 }).map((_, i) => (
        <ellipse
          key={`layer-${i}`}
          className="layer-in"
          style={{ animationDelay: `${(i * dur) / 18}s`, animationDuration: `${dur}s` }}
          cx={1080}
          cy={585 - i * 8}
          rx={(220 - i * 5) / 2}
          ry={4}
          fill={i % 3 === 0 ? "url(#grad-orange)" : "rgba(255,87,34,0.75)"}
          stroke="rgba(255,140,90,0.6)"
          strokeWidth="0.6"
        />
      ))}
    </g>
  );
};

const FilamentSpool = () => (
  <g>
    <circle cx="820" cy="330" r="30" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2" />
    <circle cx="820" cy="330" r="22" fill="none" stroke="url(#grad-orange)" strokeWidth="4"
      strokeDasharray="6 3" className="spool-spin" />
    <circle cx="820" cy="330" r="5" fill="rgba(255,255,255,0.4)" />
    <path d="M 820 358 C 900 380, 950 400, 1080 424"
      fill="none" stroke="url(#grad-orange)" strokeWidth="2.5"
      strokeDasharray="140 900" className="filament-feed" opacity="0.85" />
  </g>
);

const NozzleHead = () => (
  <g>
    <g>
      <rect x="-24" y="-18" width="48" height="30" fill="#18181B" stroke="#FF5722" strokeWidth="1.5" rx="2" />
      <rect x="-20" y="-14" width="40" height="6" fill="rgba(255,87,34,0.3)" />
      <circle cx="10" cy="-3" r="2" fill="#10B981" className="head-led" />
      <rect x="-4" y="-38" width="8" height="20" fill="rgba(255,255,255,0.3)" />
      <polygon points="-4,12 4,12 0,22" fill="#FF5722" />
      <circle cx="0" cy="22" r="4" fill="#FF7A45" opacity="0.7" filter="url(#glow)" className="tip-glow" />
      <line x1="0" y1="22" x2="0" y2="34" stroke="#FF5722" strokeWidth="1.2" opacity="0.7" />
      <animateMotion dur="7s" repeatCount="indefinite" rotate="0">
        <mpath href="#nozzle-path" />
      </animateMotion>
    </g>
  </g>
);

const Particles = () => (
  <g>
    {Array.from({ length: 8 }).map((_, i) => (
      <circle
        key={`p-${i}`}
        className="particle-rise"
        style={{ animationDelay: `${(i * 0.6).toFixed(2)}s`, animationDuration: `${4 + (i % 3)}s` }}
        cx={980 + i * 22}
        cy={560}
        r={1.4 + (i % 3) * 0.4}
        fill="rgba(255,140,90,0.7)"
      />
    ))}
  </g>
);
